import { AutomationEventTypes } from "../AutomationModuleTypes";
import _ from "lodash";

const vt = {
    STRING: "string",
    NUMBER: "number",
    BOOLEAN: "boolean",
    DATE: "date",
    DATETIME: "datetime",
    TIME: "time",
    ENUM: "enum",
    ARRAY: "array",
    ARRAY_NUMBER: "array_number",
    ARRAY_STRING: "array_string",
    ARRAY_DATE: "array_date",
    ARRAY_OBJECT: "array_objectId",
    ARRAY_BOOLEAN: "array_boolean",
    OBJECT: "object",
    MIXED: "mixed",
};

const contexts = {
    CONTACT: "contact",
    CONVERSATION: "conversation",
    BLOCK: "block",
    EVENT: "event",
    WEBHOOK: "webhook",
    SYSTEM: "system",
    CUSTOM: "custom",
};

const defaultOperators = {
    string: [
        "equal",
        "not_equal",
        "contains",
        "not_contains",
        "empty",
        "not_empty",
        "starts_with",
        "ends_with",
        "shorter_than",
        "longer_than",
    ],
    array: ["equal", "not_equal", "contains", "not_contains", "empty", "not_empty"],
    number: ["equal", "not_equal", "greater_than", "less_than", "greater_than_or_equal", "less_than_or_equal"],
    boolean: ["equal", "not_equal", "empty", "not_empty"],
    date: [
        "equal",
        "not_equal",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
        "empty",
        "not_empty",
    ],
    ObjectId: ["equal", "not_equal", "empty", "not_empty"],
    object: ["equal", "not_equal", "empty", "not_empty"],
    mixed: [
        "equal",
        "not_equal",
        "contains",
        "not_contains",
        "empty",
        "not_empty",
        "starts_with",
        "ends_with",
        "shorter_than",
        "longer_than",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
    ],
};

const ga = (
    context,
    value,
    label,
    valueType = "string",
    controlType = "input",
    subAttributes = [],
    defaultFilterValue = undefined,
    defaultOperator = undefined,
    availableOperators = undefined,
    options = undefined
) => {
    return {
        context,
        name,
        value,
        label,
        defaultOperator: defaultOperator || "equal",
        defaultFilterValue,
        availableOperators: availableOperators || defaultOperators[valueType] || ["equal", "not_equal"],
        controlType,
        options,
        subAttributes,
        valueType,
    };
};

class TicketModuleAttributeFactory {
    /**
     * @param parent {AutomationAttributesGenerator}
     */
    constructor(parent) {
        this.parent = parent;
    }

    generate() {
        const ticketCreatedEvents = this.ticketCreated();
        const ticketUpdatedEvents = this.ticketUpdated();
        const ticketStatusChangedEvents = this.ticketStatusChanged();
        const ticketAssigneeChangedEvents = this.ticketAssigneeChanged();
        const ticketClosedEvents = this.ticketClosed();

        return _.concat(
            ticketCreatedEvents,
            ticketUpdatedEvents,
            ticketStatusChangedEvents,
            ticketAssigneeChangedEvents,
            ticketClosedEvents
        );
    }

    ticketCreated() {
        const e = AutomationEventTypes.TICKET.CREATED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Ticket Created", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.ticketId`, "Ticket ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
            ]),
        ];
    }

    ticketUpdated() {
        const e = AutomationEventTypes.TICKET.UPDATED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Updated Ticlet", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.ticketId`, "Ticket ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.contactId`, "Contact ID", vt.STRING),

                ga(context, `${prefix}.statusIsChanged`, "Status Is Changed", vt.BOOLEAN),
                ga(context, `${prefix}.subjectIsChanged`, "Subject Is Changed", vt.BOOLEAN),
                ga(context, `${prefix}.resolvedIsChanged`, "Resolved Is Changed", vt.BOOLEAN),
                ga(context, `${prefix}.assignedUserIsChanged`, "Assigned User Is Changed", vt.BOOLEAN),
                ga(context, `${prefix}.resolvedDateIsChanged`, "Resolved Date Is Changed", vt.BOOLEAN),
                ga(context, `${prefix}.rateIsChanged`, "Rate Is Changed", vt.BOOLEAN),
                ga(context, `${prefix}.diff`, "Diff", vt.OBJECT, undefined, [
                    ga(context, `${prefix}.diff.$..*`, "*", vt.OBJECT),
                    ga(context, `${prefix}.diff.oldStatus`, "Old Status", vt.STRING),
                    ga(context, `${prefix}.diff.newStatus`, "New Status", vt.STRING),
                    ga(context, `${prefix}.diff.oldSubject`, "Old Subject", vt.STRING),
                    ga(context, `${prefix}.diff.newSubject`, "New Subject", vt.STRING),
                    ga(context, `${prefix}.diff.oldResolved`, "Old Resolved", vt.BOOLEAN),
                    ga(context, `${prefix}.diff.newResolved`, "New Resolved", vt.BOOLEAN),
                    ga(context, `${prefix}.diff.oldAssignedUser`, "Old Assigned User", vt.STRING),
                    ga(context, `${prefix}.diff.newAssignedUser`, "New Assigned User", vt.STRING),
                    ga(context, `${prefix}.diff.oldResolvedDate`, "Old Resolved Date", vt.DATETIME),
                    ga(context, `${prefix}.diff.newResolvedDate`, "New Resolved Date", vt.DATETIME),
                    ga(context, `${prefix}.diff.oldRate`, "Old Rate", vt.NUMBER),
                    ga(context, `${prefix}.diff.newRate`, "New Rate", vt.NUMBER),
                ]),
            ]),
        ];
    }

    ticketStatusChanged() {
        const e = AutomationEventTypes.TICKET.STATUS_CHANGED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Ticket Status Changed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.ticketId`, "Ticket ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.contactId`, "Contact ID", vt.STRING),
                ga(context, `${prefix}.diff`, "Diff", vt.OBJECT, undefined, [
                    ga(context, `${prefix}.diff.$..*`, "*", vt.OBJECT),
                    ga(context, `${prefix}.diff.oldStatus`, "Old Status", vt.STRING),
                    ga(context, `${prefix}.diff.newStatus`, "New Status", vt.STRING),
                ]),
            ]),
        ];
    }

    ticketAssigneeChanged() {
        const e = AutomationEventTypes.TICKET.ASSIGNEE_CHANGED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Ticket Assignee Changed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.ticketId`, "Ticket ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.contactId`, "Contact ID", vt.STRING),
                ga(context, `${prefix}.diff`, "Diff", vt.OBJECT, undefined, [
                    ga(context, `${prefix}.diff.$..*`, "*", vt.OBJECT),
                    ga(context, `${prefix}.diff.oldAssignee`, "Old Assignee", vt.STRING),
                    ga(context, `${prefix}.diff.newAssignee`, "New Assignee", vt.STRING),
                ]),
            ]),
        ];
    }

    ticketClosed() {
        const e = AutomationEventTypes.TICKET.CLOSED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;
        return [
            ga(context, prefix, "Ticket Closed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.ticketId`, "Ticket ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.contactId`, "Contact ID", vt.STRING),
            ]),
        ];
    }
}

export default TicketModuleAttributeFactory;
